import { FC, useMemo, useRef, useState } from "react";
import DateTimeField from "@src/lib/ui/DateTimeField";
import { cn } from "@src/lib/utils/cn";

import {
  formatDate,
  getThreeDaysFromNow,
  getTodaysDate,
} from "@src/lib/utils/dates";
import useClickOutside from "@src/lib/hooks/useClickOutside";
import { RadioGroup, RadioGroupItem } from "@src/lib/ui/RadioGroup";
import { Label } from "@src/lib/ui/Label";
import { Separator } from "@src/lib/ui/Separator";
import Text from "@src/lib/ui/Text";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";
import ClockOutlineIcon from "@src/lib/icons/18px/clock-outline";

type Props = {
  notifyUntilDate: Date | null | undefined;
  onUpdateNotifyUntilDate: (val: Date | undefined) => void;
  scheduledDateTime?: Date | null | undefined;
};

const NotificationSchedulePicker: FC<Props> = ({
  notifyUntilDate,
  onUpdateNotifyUntilDate,
  scheduledDateTime,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const scheduledDateTimeAsDate = useMemo(() => {
    if (notifyUntilDate) {
      return new Date(notifyUntilDate);
    }
    return null;
  }, [notifyUntilDate]);

  const ref = useRef(null);
  useClickOutside(ref, () => {
    if (menuVisible) {
      setMenuVisible(false);
    }
  });
  const minCalendarDate = useMemo(() => {
    return scheduledDateTime
      ? new Date(scheduledDateTime).toISOString().split("T")[0]
      : getTodaysDate();
  }, [scheduledDateTime]);
  return (
    <div>
      <div className="relative ml-2">
        {/* Styled to match the NowOrScheduledPicker trigger in Message Blasts
            so the two adjacent dropdowns visually match. */}
        <button
          type="button"
          onClick={() => setMenuVisible(true)}
          className={cn(
            "flex h-9 items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            <ClockOutlineIcon />
            <span className="truncate">
              {scheduledDateTimeAsDate
                ? `Notify daily until ${formatDate(scheduledDateTimeAsDate)}`
                : "Notify once"}
            </span>
          </div>
          <ChevronsOpenIcon className="text-muted-foreground" />
        </button>
        {menuVisible && (
          <div
            className="absolute bottom-12 right-0 flex w-[304px] flex-wrap rounded-lg border bg-background p-2 shadow-[0_9px_39px_-21px_rgba(0,0,0,0.49)] border-gray-5-alpha"
            ref={ref}
          >
            <RadioGroup
              value={scheduledDateTimeAsDate ? "daily" : "once"}
              onValueChange={(val) => {
                if (val === "once") {
                  onUpdateNotifyUntilDate(undefined);
                } else {
                  onUpdateNotifyUntilDate(
                    scheduledDateTimeAsDate ?? getThreeDaysFromNow(),
                  );
                }
              }}
            >
              <div className="flex min-h-8 cursor-pointer items-center rounded px-3 py-1.5">
                <RadioGroupItem value="once" id="notif-once" />
                <Label
                  htmlFor="notif-once"
                  className="ml-2 cursor-pointer font-normal"
                >
                  Notify once
                </Label>
              </div>
              <Separator className="my-2" />
              <div className="min-h-8 cursor-pointer rounded p-1.5 px-3 text-sm">
                <div className="flex items-center py-1.5">
                  <RadioGroupItem value="daily" id="notif-daily" />
                  <Label
                    htmlFor="notif-daily"
                    className="ml-2 cursor-pointer font-normal"
                  >
                    Notify once a day until opened
                  </Label>
                </div>
                <Text
                  variant="fine-print-title"
                  style={{ marginTop: 8, marginLeft: 24 }}
                >
                  Stop notifying on:
                </Text>
                <DateTimeField
                  value={scheduledDateTimeAsDate}
                  onChange={onUpdateNotifyUntilDate}
                  className={cn(
                    "w-full mt-2 ml-6",
                    scheduledDateTimeAsDate ? "opacity-100" : "opacity-70",
                  )}
                  type={"date"}
                  min={minCalendarDate}
                />
              </div>
            </RadioGroup>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSchedulePicker;
